import { memo, useEffect } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { makeSpinEase } from './stageConfig';

export const WHEEL_LETTERS = 'ABCDEFGHJKL'.split('');
const N = WHEEL_LETTERS.length;
const SEG = 360 / N;

const SIZE = 340;
const C = SIZE / 2;
const R = SIZE / 2 - 4;

const pt = (a, r) => [
  C + r * Math.sin((a * Math.PI) / 180),
  C - r * Math.cos((a * Math.PI) / 180),
];

export const StageWheel = memo(function StageWheel({
  size = 210,
  target = null,
  spinKey = 0,
  duration = 35,
  startAt = 0,
  onSettled,
}) {
  const rotation = useMotionValue(0);

  useEffect(() => {
    if (spinKey || target == null) return;
    rotation.set(-target * SEG);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinKey, target]);

  useEffect(() => {
    if (!spinKey || target == null) return undefined;
    const base = Math.ceil(rotation.get() / 360) * 360;
    const turns = Math.max(8, Math.round(duration * 0.72));
    const finalAngle = base + 360 * turns - target * SEG;
    const controls = animate(rotation, finalAngle, {
      duration,
      ease: makeSpinEase(duration, duration - 6),
      onComplete: () => onSettled?.(target),
    });
    if (startAt > 0) controls.time = Math.min(startAt, duration);
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinKey]);

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width={size}
      height={size}
      data-testid="stage-letter-wheel"
      style={{ display: 'block' }}
    >
      <defs>
        <radialGradient id="glassSheen" cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.5" />
          <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0" />
          <stop offset="100%" stopColor="#EDEFF2" stopOpacity="0.35" />
        </radialGradient>
        <linearGradient id="glassGlint" x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="needleRed" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF3B4E" />
          <stop offset="100%" stopColor="#B90D1D" />
        </linearGradient>
      </defs>

      {/* white sheet */}
      <circle cx={C} cy={C} r={R} fill="#FFFFFF" />

      {/* glass layer over the sheet */}
      <circle cx={C} cy={C} r={R} fill="url(#glassSheen)" />
      <ellipse
        cx={C - 42}
        cy={C - 68}
        rx={R * 0.55}
        ry={R * 0.32}
        transform={`rotate(-28 ${C - 42} ${C - 68})`}
        fill="url(#glassGlint)"
      />
      <circle cx={C} cy={C} r={R - 2} fill="none" stroke="#FFFFFF" strokeOpacity="0.6" strokeWidth="2" />
      <circle cx={C} cy={C} r={R} fill="none" stroke="#D8DCE0" strokeOpacity="0.5" strokeWidth="1" />

      {/* letters on top of the glass */}
      <motion.g style={{ rotate: rotation, originX: 0.5, originY: 0.5, willChange: 'transform' }}>
        {WHEEL_LETTERS.map((letter, k) => {
          const [lx, ly] = pt(k * SEG, R * 0.76);
          return (
            <text
              key={k}
              x={lx}
              y={ly}
              transform={`rotate(${k * SEG} ${lx} ${ly})`}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#0B0B0B"
              fontSize="88"
              fontFamily="Anton, sans-serif"
            >
              {letter}
            </text>
          );
        })}
      </motion.g>

      {/* needle — steady, no blink */}
      <polygon
        points={`${C - 9},${C} ${C + 9},${C} ${C + 2},${C - R * 0.56} ${C - 2},${C - R * 0.56}`}
        fill="url(#needleRed)"
        style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.45))' }}
        data-testid="stage-wheel-needle"
      />
      <polygon
        points={`${C - 6},${C} ${C + 6},${C} ${C},${C + 26}`}
        fill="url(#needleRed)"
        style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))' }}
      />
      <circle cx={C} cy={C} r="13" fill="#B90D1D" stroke="#E7C463" strokeWidth="2.5" />
      <circle cx={C} cy={C} r="4.5" fill="#FFFFFF" />
    </svg>
  );
});
